import React from 'react'
import { Inbox } from 'lucide-react'
import { MediaFile, MediaFolder } from '../types'
import { MediaFileItem } from './MediaFileItem'
import { MediaFolderItem } from './MediaFolderItem'
import { Spinner } from '@iws/admin-ui/spinner'

interface MediaContentProps {
  isLoading: boolean
  folders: MediaFolder[]
  files: MediaFile[]
  searchTerm: string
  selectedFiles: MediaFile[]
  multiple: boolean
  resolveUrl: (path: string) => string
  formatSize: (bytes: number) => string
  canDelete: boolean
  navigateToFolder: (id: string | null) => void
  toggleSelect: (file: MediaFile) => void
  onActivateFile?: (file: MediaFile) => void
  onDeleteFolder?: (id: string, name: string) => void
  onDeleteFile?: (id: string, name: string) => void
  viewMode?: 'grid' | 'list'
}

export const MediaContent = ({
  isLoading,
  folders,
  files,
  searchTerm,
  selectedFiles,
  multiple,
  resolveUrl,
  formatSize,
  canDelete,
  navigateToFolder,
  toggleSelect,
  onActivateFile,
  onDeleteFolder,
  onDeleteFile,
  viewMode = 'grid',
}: MediaContentProps) => {
  if (isLoading) {
    return (
      <div className="flex min-w-0 flex-1 items-center justify-center">
        <Spinner className="w-6 h-6 text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
      <div className="p-4">
        {folders.length === 0 && files.length === 0 ? (
          <div className="flex h-[40vh] flex-col items-center justify-center">
            <span className="mb-4 grid size-14 place-items-center rounded-full bg-fill"><Inbox className="size-6 text-muted-foreground" /></span>
            <p className="text-heading-md text-ink">
              {searchTerm ? 'No files found' : 'This folder is empty'}
            </p>
            <p className="mt-1 text-sm text-text-2">
              {searchTerm ? 'Try changing the search term.' : 'Upload files to get started.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {folders.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium text-ink">Folders</p>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
                  {folders.map((folder) => (
                    <MediaFolderItem
                      key={folder._id}
                      folder={folder}
                      onNavigate={navigateToFolder}
                      onDelete={onDeleteFolder}
                      canDelete={canDelete}
                    />
                  ))}
                </div>
              </div>
            )}

            {files.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium text-ink">Files</p>
                {viewMode === 'list' ? (
                  <div className="overflow-hidden rounded-lg border border-border bg-card">
                    {files.map((file) => {
                      const isSelected = !!selectedFiles.find((f) => f._id === file._id)
                      return (
                        <MediaFileItem
                          key={file._id}
                          file={file}
                          viewMode="list"
                          resolveUrl={resolveUrl}
                          formatSize={formatSize}
                          selectable={true}
                          selected={isSelected}
                          onSelect={() => toggleSelect(file)}
                          onActivate={onActivateFile ? () => onActivateFile(file) : undefined}
                          onDelete={onDeleteFile ? (id) => onDeleteFile(id, file.name) : undefined}
                          canDelete={canDelete}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(132px,1fr))] gap-3">
                    {files.map((file) => {
                      const isSelected = !!selectedFiles.find((f) => f._id === file._id)
                      return (
                        <MediaFileItem
                          key={file._id}
                          file={file}
                          viewMode="grid"
                          resolveUrl={resolveUrl}
                          formatSize={formatSize}
                          selectable={true}
                          selected={isSelected}
                          onSelect={() => toggleSelect(file)}
                          onActivate={onActivateFile ? () => onActivateFile(file) : undefined}
                          onDelete={onDeleteFile ? (id) => onDeleteFile(id, file.name) : undefined}
                          canDelete={canDelete}
                        />
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
